import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { auth, onAuthStateChanged } from '../auth';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import InviteMemberModal from '../components/groups/InviteMemberModal';
import GroupActivityFeed from '../components/groups/GroupActivityFeed';
import GroupOpenArenaSection from '../components/groups/GroupOpenArenaSection';
import GroupLeaderboard, { memberStatsFromLeaderboard } from '../components/groups/GroupLeaderboard';
import WeeklyChampionCard from '../components/groups/WeeklyChampionCard';
import GroupInviteBanner from '../components/groups/GroupInviteBanner';
import EmptyLeagueNudge from '../components/groups/EmptyLeagueNudge';
import MemberStatsChip from '../components/groups/MemberStatsChip';
import {
  fetchGroup,
  fetchGroupLeaderboard,
  leaveGroup,
  subscribeGroupActivity,
  subscribeGroupMembers,
  transferGroupOwnership,
} from '../api/groupService';
import { fetchUserProfile } from '../api/userProfileService';
import { typography } from '../design-system/tokens';

function timestampMs(value) {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value === 'number') return value;
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return null;
}

function formatSeasonStartDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function GroupDetailPage() {
  const { groupId: routeGroupId } = useParams();
  const location = useLocation();
  const groupId = routeGroupId || location.pathname.match(/^\/groups\/([^/?#]+)/)?.[1];
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [postLockSignal, setPostLockSignal] = useState(0);
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardError, setLeaderboardError] = useState('');
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser?.uid) {
        try {
          setProfile(await fetchUserProfile(firebaseUser.uid));
        } catch (_) {
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    });
  }, []);

  useEffect(() => {
    if (!groupId) return;
    fetchGroup(groupId).then(setGroup).catch(() => setGroup(null));
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return undefined;
    return subscribeGroupMembers(groupId, setMembers, () => setMembers([]));
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return undefined;
    return subscribeGroupActivity(groupId, setActivity, () => setActivity([]));
  }, [groupId]);

  const myMembership = members.find((m) => m.id === user?.uid);

  useEffect(() => {
    if (!groupId || !myMembership) return undefined;
    let cancelled = false;
    setLeaderboardLoading(true);
    setLeaderboardError('');
    fetchGroupLeaderboard(groupId)
      .then((data) => {
        if (!cancelled) setLeaderboard(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setLeaderboard(null);
          setLeaderboardError(err?.message || 'Could not load stats');
        }
      })
      .finally(() => {
        if (!cancelled) setLeaderboardLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupId, myMembership]);
  const isOwner = myMembership?.role === 'owner' || group?.ownerId === user?.uid;
  const displayName = myMembership?.displayName || profile?.displayName || user?.displayName || 'Member';

  const handleLeave = async () => {
    if (!window.confirm('Leave this group?')) return;
    setLoading(true);
    setError('');
    try {
      await leaveGroup(groupId);
      navigate('/groups');
    } catch (err) {
      setError(err?.message || 'Could not leave group');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (newOwnerId) => {
    if (!window.confirm('Transfer ownership to this member?')) return;
    setLoading(true);
    setError('');
    try {
      await transferGroupOwnership(groupId, newOwnerId);
    } catch (err) {
      setError(err?.message || 'Could not transfer ownership');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <Alert tone="warning" className="text-sm font-bold">Sign in to view this group.</Alert>
      </div>
    );
  }

  if (!group && members.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <p className="text-gray-500 font-bold">Loading group…</p>
      </div>
    );
  }

  if (!myMembership) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <Alert tone="warning" className="text-sm font-bold">You are not a member of this group.</Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-8 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <header>
          <Link to="/groups" className="text-xs font-bold text-violet-400 hover:text-violet-300">← My Groups</Link>
          <h1 className="text-2xl font-black mt-2">
            🏆 {group?.name || 'Group'}
          </h1>
          {group?.description && (
            <p className="text-sm text-gray-400 mt-1">{group.description}</p>
          )}
          <div className={`${typography.microWide} text-gray-500 mt-2 space-y-1`}>
            <p>
              {leaderboard?.season?.seasonName || group?.seasonName || 'Season 1'}
              {leaderboard?.season?.seasonNumber != null && (
                <span>{` · Season ${leaderboard.season.seasonNumber}`}</span>
              )}
              {group?.sport ? ` · ${group.sport}` : ''}
            </p>
            {(leaderboard?.season?.seasonStartAt || group?.seasonStartAt || group?.createdAt) && (
              <p className="text-[10px] text-gray-600 font-bold normal-case tracking-normal">
                Started{' '}
                {formatSeasonStartDate(
                  leaderboard?.season?.seasonStartAt
                    ?? timestampMs(group?.seasonStartAt)
                    ?? timestampMs(group?.createdAt),
                )}
              </p>
            )}
            {leaderboard?.currentLeader && (
              <p className="text-[10px] text-violet-400/90 font-bold normal-case tracking-normal">
                Current leader: {leaderboard.currentLeader.displayName}
                {' '}
                ({leaderboard.currentLeader.wins}-{leaderboard.currentLeader.losses})
              </p>
            )}
            <p>
              {members.length} member{members.length === 1 ? '' : 's'}
            </p>
          </div>
        </header>

        {error && <Alert tone="error" className="text-sm font-bold">{error}</Alert>}

        {members.length === 1 ? (
          <EmptyLeagueNudge groupId={groupId} groupName={group?.name || 'League'} />
        ) : (
          <GroupInviteBanner memberCount={members.length} onInvite={() => setInviteOpen(true)} />
        )}

        <WeeklyChampionCard
          weeklyChampion={leaderboard?.weeklyChampion}
          weekStartAt={leaderboard?.weekStartAt}
          weekEndAt={leaderboard?.weekEndAt}
          loading={leaderboardLoading}
        />

        <GroupLeaderboard
          groupId={groupId}
          groupName={group?.name || 'League'}
          data={leaderboard}
          loading={leaderboardLoading}
          error={leaderboardError}
        />

        <GroupActivityFeed
          events={activity}
          onPostLock={() => setPostLockSignal((n) => n + 1)}
          onInvite={() => setInviteOpen(true)}
        />

        <GroupOpenArenaSection
          groupId={groupId}
          groupName={group?.name || 'Group'}
          userId={user.uid}
          userName={displayName}
          subscriptionProfile={profile}
          postLockSignal={postLockSignal}
        />

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="admin" size="sm" onClick={() => setInviteOpen(true)}>
            Invite member
          </Button>
          {!isOwner && (
            <Button type="button" variant="ghost" size="sm" disabled={loading} onClick={handleLeave}>
              Leave group
            </Button>
          )}
          {isOwner && (
            <p className="text-xs text-amber-400 font-bold self-center">
              Transfer ownership before leaving (use roster actions below).
            </p>
          )}
        </div>

        <Card variant="admin" className="p-4">
          <p className={`${typography.microWide} text-gray-500 mb-3`}>Roster</p>
          <ul className="space-y-2">
            {members.map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-800 bg-black/40 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-black">{m.displayName || m.id}</p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500">{m.role}</p>
                  <MemberStatsChip
                    stats={memberStatsFromLeaderboard(leaderboard?.allTime, m.id)}
                  />
                </div>
                {isOwner && m.id !== user.uid && m.role !== 'owner' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={loading}
                    onClick={() => handleTransfer(m.id)}
                  >
                    Make owner
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        groupId={groupId}
        groupName={group?.name || 'Group'}
        fromDisplayName={displayName}
      />
    </div>
  );
}
